import React, { useRef } from 'react';
import { LightBulbIcon, XMarkIcon } from '@heroicons/react/24/solid';
import CameraPlusIcon from './CameraPlusIcon';
import PhotoPlusIcon from './PhotoPlusIcon';
import { MAX_MEAL_SUGGESTION_PHOTOS } from './mealSuggestionPhotos';
import { pickNativePhotoFiles } from './nativePhotoSource';

const MealSuggestionRequestForm = ({
  constraintInput,
  setConstraintInput,
  targetCaloriesInput,
  setTargetCaloriesInput,
  includeExisting,
  setIncludeExisting,
  hasExistingFoods,
  defaultTargetLabel,
  photoEntries,
  photoFeedback,
  onFilesSelected,
  onRemovePhoto,
  onGenerate,
  onSurprise,
  onCancel,
}) => {
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const remaining = Math.max(0, MAX_MEAL_SUGGESTION_PHOTOS - photoEntries.length);

  const handleBrowserFiles = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length) onFilesSelected(files);
    event.target.value = '';
  };

  const openPhotoSource = async (source) => {
    const nativeFiles = await pickNativePhotoFiles(source, remaining);
    if (nativeFiles === null) {
      (source === 'CAMERA' ? cameraInputRef : galleryInputRef).current?.click();
      return;
    }
    if (nativeFiles.length) onFilesSelected(nativeFiles);
  };

  return (
    <div className="meal-suggestion-request meal-anim-fade-in">
      <div className="meal-suggestion-request-title">
        <LightBulbIcon />
        What are you looking for?
      </div>
      <input
        className="meal-suggestion-text-input"
        type="text"
        value={constraintInput}
        onChange={(event) => setConstraintInput(event.target.value)}
        placeholder="e.g., something light, high protein..."
        autoFocus
        onKeyDown={(event) => {
          if (event.key === 'Enter') onGenerate();
        }}
      />

      <label className="meal-suggestion-field-label" htmlFor="meal-suggestion-target">
        Calorie target (optional)
      </label>
      <input
        id="meal-suggestion-target"
        className="meal-suggestion-text-input"
        type="number"
        min="1"
        step="10"
        value={targetCaloriesInput}
        onChange={(event) => setTargetCaloriesInput(event.target.value)}
        placeholder={defaultTargetLabel}
      />

      <section className="meal-suggestion-photo-section" aria-labelledby="meal-suggestion-photo-title">
        <div className="meal-suggestion-photo-heading">
          <div>
            <div id="meal-suggestion-photo-title" className="meal-suggestion-photo-title">Ingredient photos (optional)</div>
            <div className="meal-suggestion-photo-help">
              Show ingredients or labels you have. Photos guide this recipe and are not added to your meal log.
            </div>
          </div>
          <span className="meal-suggestion-photo-count">{photoEntries.length} of {MAX_MEAL_SUGGESTION_PHOTOS}</span>
        </div>

        <input
          ref={cameraInputRef}
          data-photo-source="camera"
          className="hidden-file-input"
          type="file"
          accept="image/*;capture=camera"
          capture="environment"
          onChange={handleBrowserFiles}
        />
        <input
          ref={galleryInputRef}
          data-photo-source="gallery"
          className="hidden-file-input"
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          onChange={handleBrowserFiles}
        />
        <div className="meal-suggestion-photo-actions">
          <button type="button" onClick={() => openPhotoSource('CAMERA')} disabled={!remaining}>
            <CameraPlusIcon /> Take photo
          </button>
          <button type="button" onClick={() => openPhotoSource('PHOTOS')} disabled={!remaining}>
            <PhotoPlusIcon /> Choose photos
          </button>
        </div>

        {photoEntries.length > 0 && (
          <div className="meal-suggestion-photo-previews" aria-label="Attached ingredient photos">
            {photoEntries.map((entry, index) => (
              <div className="meal-suggestion-photo-preview" key={`${entry.file.name}-${entry.file.lastModified}-${index}`}>
                <img src={entry.previewUrl} alt={`Ingredient reference ${index + 1}`} />
                <button
                  type="button"
                  onClick={() => onRemovePhoto(index)}
                  aria-label={`Remove ingredient photo ${index + 1}`}
                  title="Remove photo"
                >
                  <XMarkIcon />
                </button>
              </div>
            ))}
          </div>
        )}
        {photoFeedback.length > 0 && (
          <div className="meal-suggestion-photo-feedback" role="status" aria-live="polite">
            {photoFeedback.map((message, index) => <div key={`${message}-${index}`}>{message}</div>)}
          </div>
        )}
      </section>

      {hasExistingFoods && (
        <label className="meal-suggestion-existing-toggle">
          <input
            type="checkbox"
            checked={includeExisting}
            onChange={(event) => setIncludeExisting(event.target.checked)}
          />
          Integrate existing foods
        </label>
      )}

      <div className="meal-suggestion-request-actions">
        <button type="button" className="primary" onClick={onGenerate}>Generate</button>
        {!constraintInput.trim() && (
          <button type="button" className="surprise" onClick={onSurprise}>🎲 Surprise Me</button>
        )}
        <button type="button" className="cancel" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
};

export default MealSuggestionRequestForm;
